import { TextAppearanceKeys, TextEmphasisKeys, TextSizeKeys, ThankyouDisplayModes } from '@/constants/enum';
import { bannerStatusCards } from '@/constants/options';
import { handleGetTextColor, handleGetTextEmphasis, handleGetTextSize } from '@/helpers/common';
import BannerStatusCard from '@/pages/Checkout/components/BannerStatusCard';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IResponseApi } from '@/types/apis/response';
import { Card, Icon, Tabs, Text } from '@shopify/polaris';
import { ArrowLeftIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PreviewStyled } from './styled';

interface IProps {
  children?: any;
  top: number;
  displayMode: ThankyouDisplayModes;
  dataAppearance?: IResponseApi.ICheckoutAppearance;
  isInOnboarding?: boolean;
}

const PreviewThankyouOrder = ({ top, displayMode, dataAppearance, isInOnboarding }: IProps) => {
  const dataSettings = useSelector(dataSettingsSelector);

  const [type, setType] = useState<'thank' | 'order'>('thank');

  const [selected, setSelected] = useState(type === 'thank' ? 0 : 1);

  const handleTabChange = useCallback((selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
    setType(selectedTabIndex === 0 ? 'thank' : 'order');
  }, []);

  const tabs = [
    {
      id: 'thank',
      content: 'Thank you',
    },
    {
      id: 'order',
      content: 'Order status',
    },
  ];

  const selectedStatus = useMemo(() => {
    const status = dataAppearance?.status;
    return bannerStatusCards.find((item) => item.value === status) || bannerStatusCards[0];
  }, [dataAppearance?.status]);

  const plainTextColor = useMemo(() => {
    return handleGetTextColor(dataAppearance?.textColor || TextAppearanceKeys.base);
  }, [dataAppearance?.textColor]);

  const plainTextSize = useMemo(() => {
    return handleGetTextSize(dataAppearance?.textSize || TextSizeKeys.base);
  }, [dataAppearance?.textSize]);

  const plainTextEmphasis = useMemo(() => {
    return handleGetTextEmphasis(dataAppearance?.textEmphasis || TextEmphasisKeys.base);
  }, [dataAppearance?.textEmphasis]);

  return (
    <PreviewStyled top={top} generalSetting={dataSettings}>
      <Card padding={'0'}>
        <div className="preview-header">
          <Text as="span" variant="bodyMd">
            BANNER PREVIEW
          </Text>
        </div>
        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} fitted>
          <div className="preview-content">
            {type === 'thank' ? (
              <div className="d-flex gap-8 align-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="39" height="40" viewBox="0 0 20 21" fill="none">
                  <path
                    d="M13.5714 7.7606L9.87681 13.321C9.67683 13.622 9.25122 13.6641 8.99613 13.4082L6.42857 10.832M18 10.332C18 14.7503 14.4183 18.332 10 18.332C5.58172 18.332 2 14.7503 2 10.332C2 5.91375 5.58172 2.33203 10 2.33203C14.4183 2.33203 18 5.91375 18 10.332Z"
                    stroke="#afafaf"
                    strokeWidth="0.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="d-flex flex-column">
                  <p className="text-gray">Confirmation #ABC123EXAMPLE</p>
                  <p className="text-gray">Thank you, Omega!</p>
                </div>
              </div>
            ) : (
              <div className="d-flex gap-8">
                <Icon source={ArrowLeftIcon} tone="subdued" />
                <div className="d-flex flex-column">
                  <p className="text-gray">Order #1001</p>
                  <p className="text-gray">Confirmed August 6</p>
                </div>
              </div>
            )}

            {displayMode === ThankyouDisplayModes.banner ? (
              <BannerStatusCard
                backgroundColor={selectedStatus?.backgroundColor}
                contentColor={selectedStatus?.contentColor}
                icon={selectedStatus?.icon}
                messageColor={dataAppearance?.textColor}
                messageSize={dataAppearance?.textSize}
                messageEmphasis={dataAppearance?.textEmphasis}
                dataCard={{
                  title: isInOnboarding ? 'Delivery estimates' : dataAppearance?.title || '',
                  content: [
                    'Standard shipping: Delivery from 6 Jun to 8 Jun',
                    'Express shipping: Delivery from 8 Jun to 11 Jun ',
                  ],
                  collapse: isInOnboarding || !!dataAppearance?.collapsible,
                  close: isInOnboarding || !!dataAppearance?.onDismiss,
                  status: dataAppearance?.status,
                }}
              />
            ) : (
              <p
                style={{
                  marginTop: '8px',
                  fontSize: plainTextSize,
                  color: plainTextColor,
                  fontWeight: plainTextEmphasis === TextEmphasisKeys.bold ? plainTextEmphasis : undefined,
                  fontStyle: plainTextEmphasis === TextEmphasisKeys.italic ? plainTextEmphasis : undefined,
                }}
              >
                Standard shipping: Delivery from 6 Jun to 8 Jun
              </p>
            )}
          </div>
        </Tabs>
      </Card>
    </PreviewStyled>
  );
};

export default memo(PreviewThankyouOrder);
